import { RHFTextField } from '@components/ui/forms/fields';
import { Alert, Box, Typography } from '@mui/material';

import type { FormFieldConfig, FormStepProps } from './types';

/** Map a field width keyword to its 12-column grid span. */
const SPAN: Record<NonNullable<FormFieldConfig['width']>, number> = {
  full: 12,
  half: 6,
  third: 4,
  quarter: 3,
};

/**
 * Config-driven data-capture step. Renders a responsive 12-column grid of
 * react-hook-form-bound fields via the shared RHFTextField. Validation and
 * error messages come from the form's zod resolver; the consuming hook owns
 * the form `control`.
 */
export const FormStep: React.FC<FormStepProps> = ({ config, control }) => {
  return (
    <Box sx={{ px: 3, py: 2.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {config.title}
      </Typography>
      {config.description && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {config.description}
        </Typography>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
        {config.fields.map(field => (
          <Box
            key={field.name}
            sx={{ gridColumn: { xs: 'span 12', sm: `span ${SPAN[field.width ?? 'full']}` } }}
          >
            <RHFTextField
              control={control}
              name={field.name}
              label={field.label}
              type={field.type}
              placeholder={field.placeholder}
              required={field.required}
              disabled={field.disabled}
            />
          </Box>
        ))}
      </Box>

      {config.note && (
        <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
          {config.note}
        </Alert>
      )}
    </Box>
  );
};
